import { useQuery } from "hooks/useQuery";
import { useNavigate, useParams } from "react-router-dom";
import { CourseOrderService } from "services/courseOrder";
import { CourseService } from "services/courseService";
import { OrderUserTypes, UserTypes } from "./orderPage.types";
import { useEffect, useState, useCallback } from "react";
import { BreadcrumbsParamsType, useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import { TasksService } from "services/tasks";
import {OpenPublishingTaskResult} from "../../../../services/tasks/models/tasksTypes";
import {useTranslation} from "react-i18next";

export const useViewCourseOrder = ({
  userType,
  breadcrumbs,
}: {
  userType: UserTypes;
  breadcrumbs: (props: {
    orderId?: number | string;
    serialNumber?: string;
    serialGuid?: string;
  }) => BreadcrumbsParamsType[];
}) => {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { orderId, serialGID } = useParams();
  const {i18n} = useTranslation();

  // local refresh key to force refetch after updates
  const [triggerOrderUpdate,  setTriggerOrderUpdate] = useState(false);
  
  //Open Task Api
  const { data: openedTask, loading: isTaskLoading } = useQuery({
    queryFn: async (_: string) => {
      const res= await TasksService.openPublishingOrderTask(serialGID ?? "");
      return {
        data: res,
        hasError: false,
        code: 200,
        description: "",
      };
    },
    options: {
      enabled: !!serialGID,
    },
    triggers: [serialGID],
  });

  //Order Api
  const { data: order, loading: isOrderLoading } = useQuery({
    queryFn: async () => {
      const id = orderId ? +orderId : openedTask?.courseOrderId ? openedTask.courseOrderId : 0;

      return await CourseOrderService.getCourseOrderById(id);
    },
    options: {
      enabled: !!openedTask?.courseOrderId || !!orderId,
    },
    triggers: [openedTask?.courseOrderId, orderId, i18n.language, triggerOrderUpdate],
    /*  onError: () => {
      navigate("/home/tasks");
    }, */
    initialLoading: !!orderId,
  });

  //Course Api
  const { data: course, loading: courseLoading } = useQuery({
    queryFn: async () => {
      const id = order?.courseId ? order.courseId : 0;

      return await CourseService.getCourseById(id);
    },
    triggers: [order?.courseId, i18n.language],
    options: { enabled: !!order?.courseId },
  });

  useEffect(() => {
    if ((openedTask && !openedTask.courseOrderId) || (orderId && !order && !isOrderLoading)) {
      navigate(!!serialGID ? "/home/tasks" : "/home/orders");
    }
  }, [openedTask]);

  useEffect(() => {
    setBreadcrumbs(
      breadcrumbs({
        orderId: order?.id,
        serialNumber: order?.serialNumber,
        serialGuid: serialGID,
      }),
    );
  }, [order]);


  return {
    orderId,
    order,
    course,
    openedTask,
    courseLoading: courseLoading,
    isOrderLoading: isOrderLoading || isTaskLoading, 
      setTriggerOrderUpdate,
  };
};
